import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { Navigate } from "react-router-dom";
import ApiService from "@/services/ApiService";
import { Button } from "@/components/ui/button";
import {
  clearTenantSelection,
  getTenantSelection,
  openTenant,
  TenantSelection,
} from "./tenantSelection";

export type Membership = {
  id: number;
  name: string;
  type: "agent" | "company";
  role: string;
  capabilities: string[];
};
export type AdminSession = {
  user: { id: number; name: string; email: string };
  is_platform_admin: boolean;
  platform_role: string | null;
  platform_capabilities: string[];
  memberships: Membership[];
  agent: { id: number; name: string } | null;
  company: { id: number; name: string } | null;
  mfa_enabled: boolean;
  mail_enabled: boolean;
  company_posting: boolean;
};
const Context = createContext<{
  session: AdminSession;
  tenant: TenantSelection | null;
  can: (capability: string) => boolean;
  refresh: () => Promise<void>;
} | null>(null);
export function useAdminSession() {
  const context = useContext(Context);
  if (!context) throw new Error("Administrator session is unavailable");
  return context;
}
export function AdminSessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<AdminSession | null>(null);
  const [error, setError] = useState("");
  const [expired, setExpired] = useState(false);
  const refresh = async () => {
    const { data } = await ApiService.get("auth/me");
    const current = data.data as AdminSession;
    current.memberships ??= [];
    current.platform_capabilities ??= [];
    const selection = getTenantSelection();
    if (
      String(selection?.actorId) !== String(current.user.id) ||
      (!current.is_platform_admin &&
        selection &&
        !current.memberships.some(
          (m) => m.id === selection.id && m.type === selection.type,
        ))
    )
      clearTenantSelection();
    if (
      !getTenantSelection() &&
      !current.is_platform_admin &&
      current.memberships.length === 1
    ) {
      const m = current.memberships[0];
      sessionStorage.setItem(
        "job-admin-tenant",
        JSON.stringify({ ...m, actorId: current.user.id }),
      );
    }
    localStorage.setItem("user", JSON.stringify(current.user));
    setSession(current);
  };
  useEffect(() => {
    let live = true;
    refresh().catch((failure) => {
      if (!live) return;
      if (failure.response?.status === 401) {
        localStorage.removeItem("token");
        clearTenantSelection();
        setExpired(true);
      } else
        setError(
          failure.response?.status === 403
            ? "管理画面の利用権限がありません。"
            : "アカウント情報を取得できませんでした。",
        );
    });
    return () => {
      live = false;
    };
  }, []);
  if (expired) return <Navigate to="/login" replace />;
  if (error)
    return (
      <div className="p-8 space-y-4" role="alert">
        <p>{error}</p>
        <Button onClick={() => window.location.reload()}>
          再読み込み
        </Button>{" "}
        <Button
          variant="outline"
          onClick={() => {
            localStorage.removeItem("token");
            clearTenantSelection();
            window.location.assign("/login");
          }}
        >
          ログイン画面へ
        </Button>
      </div>
    );
  if (!session)
    return (
      <p className="p-8" role="status">
        アカウントを確認しています…
      </p>
    );
  const selection = getTenantSelection();
  const membership = session.memberships.find(
    (m) => m.id === selection?.id && m.type === selection?.type,
  );
  const tenant = selection ? { ...selection, ...(membership ?? {}) } : null;
  const capabilities = session.is_platform_admin
    ? session.platform_capabilities
    : (membership?.capabilities ?? []);
  return (
    <Context.Provider
      value={{
        session,
        tenant,
        can: (capability) => capabilities.includes(capability),
        refresh,
      }}
    >
      {children}
    </Context.Provider>
  );
}
export function OrganizationSelector() {
  const { session } = useAdminSession();
  return (
    <div className="max-w-4xl mx-auto p-6 space-y-5">
      <h1 className="text-2xl font-bold">管理する組織を選択</h1>
      {session.memberships.map((m) => (
        <div
          className="border rounded-xl p-5 flex justify-between items-center gap-4"
          key={m.type + m.id}
        >
          <div>
            <h2 className="font-semibold">{m.name}</h2>
            <p className="text-sm text-muted-foreground">
              {m.type === "agent" ? "紹介会社" : "企業"} · {m.role}
            </p>
          </div>
          <Button
            onClick={() => openTenant({ ...m, actorId: session.user.id }, "/")}
          >
            選択
          </Button>
        </div>
      ))}
      {!session.memberships.length && <p>利用可能な組織がありません。</p>}
    </div>
  );
}
